import { useEffect, useState } from 'react'
import {
  Box, Button, Checkbox, CloseButton, Dialog, HStack, Input, NativeSelect, Spinner, Text, VStack,
} from '@chakra-ui/react'
import { toaster } from '@/components/ui/toaster'
import { ResponsiveModal } from '../../../shared'
import { useFollowUpItems } from '../../../../hooks/useFollowUp'
import { useCreateCallBatch } from '../../../../hooks/useFollowUpCalls'
import { useAvailableCanvassers } from '../../../../hooks/useCampaign'
import { useLimits } from '../../../../hooks/useLimits'
import { extractApiMessage } from '../../../../lib/apiError'
import { formatPhone } from '../../../../lib/contactMethods'
import { toLocalDateStr } from '../routes/routeDates'
import type { CreateCallBatchResult, FollowUpKind } from '../../../../types/followUp'

const PAGE_SIZE = 200

export interface CreateCallBatchModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  kind: FollowUpKind
  customKindId?: string | null
  /** Shown in the title, e.g. "Call-back". */
  kindLabel?: string
}

/** One line per non-zero exclusion, copy exact. */
export function exclusionLines(excluded: CreateCallBatchResult['excluded']): string[] {
  const lines: string[] = []
  if (excluded.doNotContact > 0) lines.push(`${excluded.doNotContact} marked do not contact`)
  if (excluded.phoneRestricted > 0) lines.push(`${excluded.phoneRestricted} on a do-not-call list`)
  if (excluded.noPhone > 0) lines.push(`${excluded.noPhone} with no phone`)
  if (excluded.notOpen > 0) lines.push(`${excluded.notOpen} already closed`)
  if (excluded.alreadyQueued > 0) lines.push(`${excluded.alreadyQueued} already on another call list`)
  return lines
}

/** Pick open people of one kind, optionally assign a canvasser and day, and make a call list. */
export function CreateCallBatchModal({
  isOpen, onClose, campaignId, kind, customKindId = null, kindLabel,
}: CreateCallBatchModalProps) {
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [label, setLabel] = useState('')
  const [date, setDate] = useState(() => toLocalDateStr(new Date()))
  const [grantId, setGrantId] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)

  // Each open starts fresh.
  useEffect(() => {
    if (!isOpen) return
    setPage(1)
    setSelected(new Set())
    setLabel('')
    setDate(toLocalDateStr(new Date()))
    setGrantId('')
    setServerError(null)
  }, [isOpen])

  const { data: itemsPage, isLoading } = useFollowUpItems(campaignId, {
    kind,
    ...(customKindId ? { customKindId } : {}),
    status: 'Open',
    page,
    pageSize: PAGE_SIZE,
  })
  const rows = itemsPage?.items ?? []
  const total = itemsPage?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const { data: available } = useAvailableCanvassers(campaignId, isOpen && date ? date : undefined)
  const canvassers = available?.canvassers ?? []

  // Caps are pre-flight only: unknown while loading, and never a reason to disable submit.
  const maxItems = useLimits().data?.inputCaps?.maxCallBatchItems
  const overCap = maxItems !== undefined && selected.size > maxItems

  const { mutateAsync: createBatch, isPending } = useCreateCallBatch(campaignId)

  const allOnPageSelected = rows.length > 0 && rows.every(r => selected.has(r.id))
  const toggleOne = (id: string) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const togglePage = () => setSelected(prev => {
    const next = new Set(prev)
    if (allOnPageSelected) rows.forEach(r => next.delete(r.id)); else rows.forEach(r => next.add(r.id))
    return next
  })

  const handleSubmit = async () => {
    setServerError(null)
    try {
      const result = await createBatch({
        kind,
        customKindId,
        itemIds: [...selected],
        assignedGrantId: grantId || null,
        // The day schedules a canvasser's list; an unassigned list carries none (the date only finds who's free).
        scheduledDate: grantId ? (date || null) : null,
        label: label.trim() || undefined,
      })
      const lines = exclusionLines(result.excluded)
      toaster.create({
        title: `Call list ready: ${result.included} people`,
        description: lines.length > 0 ? (
          <VStack align="start" gap={0}>
            {lines.map(l => <Text key={l} fontSize="sm">{l}</Text>)}
          </VStack>
        ) : undefined,
        type: 'success',
        duration: lines.length > 0 ? 8000 : 4000,
      })
      onClose()
    } catch (err) {
      setServerError(extractApiMessage(err, 'Could not make the call list.'))
    }
  }

  const title = `New call list${kindLabel ? ` · ${kindLabel}` : ''}`

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>{title}</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch" minW={0}>
              <VStack gap={3} align="stretch">
                <Box>
                  <Text asChild fontSize="xs" fontWeight="semibold" color="muted" mb={1} display="block">
                    <label htmlFor="call-batch-label">Name (optional)</label>
                  </Text>
                  <Input id="call-batch-label" minH="44px" value={label} maxLength={80}
                    placeholder="e.g. Saturday call-backs" onChange={(e) => setLabel(e.target.value)} />
                </Box>
                <Box>
                  <Text asChild fontSize="xs" fontWeight="semibold" color="muted" mb={1} display="block">
                    <label htmlFor="call-batch-date">Day (optional)</label>
                  </Text>
                  <Input id="call-batch-date" type="date" minH="44px" value={date}
                    onChange={(e) => setDate(e.target.value)} />
                </Box>
                <Box>
                  {/* A NativeSelect must never sit inside Field.Root (it silently disables it). */}
                  <Text asChild fontSize="xs" fontWeight="semibold" color="muted" mb={1} display="block">
                    <label htmlFor="call-batch-canvasser">Canvasser (optional)</label>
                  </Text>
                  <NativeSelect.Root>
                    <NativeSelect.Field id="call-batch-canvasser" value={grantId} minH="44px"
                      onChange={(e) => setGrantId(e.target.value)}>
                      <option value="">Unassigned</option>
                      {canvassers.map(c => <option key={c.grantId} value={c.grantId}>{c.fullName}</option>)}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Box>
              </VStack>

              <Box>
                <HStack justify="space-between" mb={2} gap={2} flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="semibold">
                    {selected.size} selected of {total} open
                  </Text>
                  {rows.length > 0 && (
                    <Checkbox.Root size="sm" checked={allOnPageSelected} onCheckedChange={togglePage}>
                      <Checkbox.HiddenInput />
                      <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                      <Checkbox.Label>Select all on this page</Checkbox.Label>
                    </Checkbox.Root>
                  )}
                </HStack>
                {isLoading ? (
                  <VStack py={6}><Spinner color="accent" /></VStack>
                ) : rows.length === 0 ? (
                  <Text fontSize="sm" color="muted">No open people to call.</Text>
                ) : (
                  <VStack gap={0} align="stretch" maxH="320px" overflowY="auto" borderWidth="1px" borderColor="border" borderRadius="md">
                    {rows.map(r => (
                      <Checkbox.Root
                        key={r.id}
                        size="sm"
                        minH="48px"
                        px={3}
                        py={2}
                        borderBottomWidth="1px"
                        borderColor="border"
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggleOne(r.id)}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                        <Checkbox.Label>
                          <Text fontSize="sm">{r.displayName}</Text>
                          <Text fontSize="xs" color="muted">
                            {r.phone ? formatPhone(r.phone) : 'No phone on file'}
                          </Text>
                        </Checkbox.Label>
                      </Checkbox.Root>
                    ))}
                  </VStack>
                )}
                {pageCount > 1 && (
                  <HStack justify="space-between" mt={2}>
                    <Button size="sm" variant="outline" minH="44px" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                    <Text fontSize="xs" color="muted">Page {page} of {pageCount}</Text>
                    <Button size="sm" variant="outline" minH="44px" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>Next</Button>
                  </HStack>
                )}
              </Box>

              {overCap && (
                <Text fontSize="xs" color="info.fg" role="status">
                  A call list takes up to {maxItems} people; the server will refuse this one.
                </Text>
              )}
              {!!serverError && <Text fontSize="sm" color="danger.fg" role="alert">{serverError}</Text>}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <HStack gap={3} justify="flex-end" w="100%">
              <Button variant="ghost" minH="44px" onClick={onClose}>Cancel</Button>
              <Button colorPalette="brand" minH="44px" loading={isPending} disabled={selected.size === 0}
                onClick={handleSubmit}>
                Make call list
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
